import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { MDBDataTableV5 } from "mdbreact";
import {
  CircularProgress,
  Drawer,
  TextField,
  InputAdornment,
  Pagination,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { styled } from "@mui/material/styles";
import DatePicker from "react-datepicker";

// components
import Sidebar from "./components/sidebar";
import Header from "./components/header";

const mySwal = withReactContent(Swal);

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-20%`,
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
      maxWidth: "80%",
    }),
  }),
);

const Validation = ({ role, token }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0,
    hasMore: false,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [date, setDate] = useState(new Date().toISOString().substring(0, 10));
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [type, setType] = useState("Zuellig");
  const [validationStatus, setValidationStatus] = useState("Pending");
  const [selectedHcpIndex, setSelectedHcpIndex] = useState(0);
  const [openSidebar, setOpenSidebar] = useState(true);
  const checkboxList = useRef([]);
  const [forceTableRender, setForceTableRender] = useState(0);
  const [allRedemptionIds, setAllRedemptionsIds] = useState([]);

  const date3monthsAgo = new Date();
  date3monthsAgo.setMonth(date3monthsAgo.getMonth() - 3);

  // Debounced search function
  const [searchDebounce, setSearchDebounce] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch data function with pagination and search
  const fetchData = useCallback(
    async (
      page = 1,
      search = "",
      resetPagination = false,
      customPageSize = null,
    ) => {
      if (!role || !token) return;

      if (role === "hcp" || role === "sales") {
        mySwal
          .fire({
            title: "Error",
            text: "Unauthorized Access",
            icon: "error",
            allowOutsideClick: false,
          })
          .then((click) => {
            if (click.isConfirmed) {
              navigate("/admin/login");
            }
          });
        return;
      }

      setLoading(true);

      const currentPageSize = customPageSize || pagination.pageSize;
      const params = {
        role,
        token,
        date,
        type,
        validationStatus,
        startDate,
        endDate,
        page,
        pageSize: currentPageSize,
        search,
      };

      console.log("API Request params:", {
        page,
        type,
        validationStatus,
        search,
        pageSize: currentPageSize,
      });

      try {
        const res = await axios.post("/api/admin/getValidationList", params);
        console.log("API Response:", {
          dataLength: res.data.data.length,
          pagination: res.data.pagination,
          firstRowData: res.data.data[0], // Log first row to see structure
          data: res?.data?.data,
        });

        setAllRedemptionsIds(res?.data?.allIds);
        setData(res.data.data);
        setPagination(res.data.pagination);

        // Force table re-render after a brief delay to ensure checkboxes appear
        setTimeout(() => {
          setForceTableRender((prev) => prev + 1);
        }, 100);

        // If current page is beyond available pages and there are pages available, redirect to last page
        if (
          page > res.data.pagination.totalPages &&
          res.data.pagination.totalPages > 0
        ) {
          fetchData(res.data.pagination.totalPages, search);
          return;
        }

        if (resetPagination) {
          checkboxList.current = [];
        }
      } catch (err) {
        if (err.response?.data?.error === "Token Expired") {
          mySwal
            .fire({
              title: "Error",
              text: err.response.data.error,
              icon: "error",
              allowOutsideClick: false,
            })
            .then((click) => {
              if (click.isConfirmed) {
                navigate("/admin/login");
              }
            });
        } else {
          mySwal.fire(
            "Error",
            err.response?.data?.error || "An error occurred",
            "error",
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [
      role,
      token,
      date,
      type,
      validationStatus,
      startDate,
      endDate,
      pagination.pageSize,
      navigate,
    ],
  );

  // Handle page change
  const handlePageChange = useCallback(
    (event, newPage) => {
      fetchData(newPage, searchDebounce);
    },
    [fetchData, searchDebounce],
  );

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (event) => {
      const newPageSize = parseInt(event.target.value);
      console.log("Changing page size to:", newPageSize);
      setPagination((prev) => ({ ...prev, pageSize: newPageSize, page: 1 }));
      checkboxList.current = []; // Clear selections when changing page size
      fetchData(1, searchDebounce, true, newPageSize);
    },
    [fetchData, searchDebounce],
  );

  // Handle search
  useEffect(() => {
    if (searchDebounce !== undefined) {
      fetchData(1, searchDebounce, true);
    }
  }, [searchDebounce]);

  // Move cancel above useMemo and wrap in useCallback
  const cancel = useCallback(
    (redemptionId) => {
      setLoading(true);
      axios
        .post("/api/admin/cancelRedemption", { role, token, redemptionId })
        .then((res) => {
          setLoading(false);
          setValidationStatus("Pending");
        })
        .catch((err) => {
          setLoading(false);
          if (err.response.data.error === "Token Expired") {
            mySwal
              .fire({
                title: "Error",
                text: err.response.data.error,
                icon: "error",
                allowOutsideClick: false,
              })
              .then((click) => {
                if (click.isConfirmed) {
                  navigate("/admin/login");
                }
              });
          } else {
            mySwal.fire("Error", err.response.data.error, "error");
          }
        });
    },
    [role, token, navigate],
  );

  // Memoize approveData and hcpNameList to avoid memory leaks and unnecessary recalculation
  const { approveData, hcpNameList } = useMemo(() => {
    if (validationStatus !== "Approve" || data.length === 0) {
      return { approveData: [], hcpNameList: [] };
    }

    const approveData = [];
    const hcpMap = new Map(); // Use Map for better performance

    console.log("Processing data for approval: ", data);
    for (let i = 0; i < data.length; i++) {
      const item = data[i];

      // Process approve data
      approveData.push({
        requestDate: item.requestDate,
        redemptionDate: item.redemptionDate,
        redemptionType: item.redemptionType,
        accountName: item.accountName,
        accountCode: item.accountCode,
        purchasecode: item.purchasecode,
        hcpPic: item.hcpPic,
        hcpContact: item.hcpContact,
        hcpAddress: item.hcpAddress,
        skuRedeemed: item.skuRedeemed,
        skuCode: item.skuCode,
        quantityRedeemed: item.quantityRedeemed,
        patientLastFourDigit: item.patientLastFourDigit,
        remarks: item.remarks,
        action: (
          <div>
            <button
              type="button"
              className="btn btn-primary admin-cancel-btn shadow-none"
              onClick={() => cancel(item.redemptionId)}
            >
              Cancel
            </button>
          </div>
        ),
      });

      // Process HCP data using Map for efficiency
      if (!hcpMap.has(item.hcpId)) {
        hcpMap.set(item.hcpId, {
          hcpId: item.hcpId,
          hcpName: item.accountName,
          hcpCode: item.accountCode,
          hcpPic: item.hcpPic,
          hcpContact: item.hcpContact,
          hcpAddress: item.hcpAddress,
          purchaseName: item.purchaseName,
          purchasecode: item.purchasecode,
          cvseName: item.cvseName,
          cvseContact: item.cvseContact,
          soldToCode: item.soldToCode,
          shipToCode: item.shipToCode,
          redemptions: [],
        });
      }

      hcpMap.get(item.hcpId).redemptions.push({
        skuRedeemed: item.redeemSku,
        skuCode: item.skuCode,
        qty: item.qty,
        quantityRedeemed: item.quantityRedeemed,
        patientLastFourDigit: item.patientLastFourDigit,
      });
    }

    console.log("approveData: ", approveData);
    return {
      approveData,
      hcpNameList: Array.from(hcpMap.values()),
    };
  }, [data, validationStatus, cancel]);

  useEffect(() => {
    if (!role || !token) {
      navigate("/admin/login");
      return;
    }
    fetchData(1, searchDebounce, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, type, validationStatus]);

  // Memoized table data for better performance - prevents unnecessary recalculations
  const pendingTableData = useMemo(
    () => ({
      columns: [
        {
          field: "requestDate",
          label: "Request Date",
        },
        {
          field: "redemptionDate",
          label: "Redemption Date",
        },
        {
          field: "redemptionType",
          label: "Redemption Type",
        },
        {
          field: "accountName",
          label: "Account Name",
        },
        {
          field: "accountCode",
          label: "Account Code",
        },
        {
          field: "purchasecode",
          label: type === "Zuellig" ? "ZP Code" : "Antah Code",
        },
        {
          field: "hcpPic",
          label: "HCP PIC",
        },
        {
          field: "mpgRedeemed",
          label: "MPG Redeemed",
        },
        {
          field: "skuRedeemed",
          label: "SKU Redeemed",
        },
        {
          field: "skuCode",
          label: "SKU Code",
        },
        {
          field: "quantityRedeemed",
          label: "Quantity Redeemed",
        },
        {
          field: "patientLastFourDigit",
          label: "Patient Last 4-digit",
        },
        {
          field: "seName",
          label: "SE Name",
        },
        {
          field: "seContact",
          label: "SE Contact",
        },
        {
          field: "remarks",
          label: "Remarks",
        },
        {
          field: "serialNo",
          label: "Serial No.",
        },
        {
          field: "or",
          label: "#OR",
        },
      ],
      rows: data || [],
    }),
    [data, type],
  );

  // Memoized approve table data
  const approveTableData = useMemo(
    () => ({
      columns: [
        {
          field: "requestDate",
          label: "Request Date",
        },
        {
          field: "redemptionDate",
          label: "Redemption Date",
        },
        {
          field: "redemptionType",
          label: "Redemption Type",
        },
        {
          field: "accountName",
          label: "Account Name",
        },
        {
          field: "accountCode",
          label: "Account Code",
        },
        {
          field: "purchasecode",
          label: type === "Zuellig" ? "ZP Code" : "Antah Code",
        },
        {
          field: "hcpPic",
          label: "HCP PIC",
        },
        {
          field: "hcpContact",
          label: "HCP Contact",
        },
        {
          field: "hcpAddress",
          label: "HCP Address",
        },
        {
          field: "skuRedeemed",
          label: "SKU Redeemed",
        },
        {
          field: "skuCode",
          label: "SKU Code",
        },
        {
          field: "quantityRedeemed",
          label: "Quantity Redeemed",
        },
        {
          field: "patientLastFourDigit",
          label: "Patient Last 4-digit",
        },
        {
          field: "remarks",
          label: "Remarks",
        },
        {
          field: "action",
          label: "Action",
        },
      ],
      rows: approveData || [],
    }),
    [approveData, type],
  );

  const handleDate = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleFilter = () => {
    fetchData(1, searchDebounce, true);
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    fetchData(1, searchDebounce, true);
  };

  const selectRedemption = (e) => {
    if (e.checked) {
      checkboxList.current.push(e);
    } else {
      checkboxList.current = checkboxList.current.filter(
        (c) => c.redemptionId !== e.redemptionId,
      );
    }
  };

  const selectAllRedemptions = (e) => {
    if (data.length > 0) {
      if (e[0].checked) {
        checkboxList.current = e;
      } else {
        checkboxList.current = [];
      }
    }
  };

  const approve = () => {
    if (checkboxList.current.length > 0) {
      setLoading(true);
      const sendThis = {
        role,
        token,
        redemptionIds: checkboxList.current.map((c) => c.redemptionId),
      };
      axios
        .post("/api/admin/approveRedemption", sendThis)
        .then((res) => {
          setLoading(false);
          setValidationStatus("Approve");
          checkboxList.current = [];
          fetchData(1, searchDebounce, true);
        })
        .catch((err) => {
          setLoading(false);
          if (err.response.data.error === "Token Expired") {
            mySwal
              .fire({
                title: "Error",
                text: err.response.data.error,
                icon: "error",
                allowOutsideClick: false,
              })
              .then((click) => {
                if (click.isConfirmed) {
                  navigate("/admin/login");
                }
              });
          } else {
            mySwal.fire("Error", err.response.data.error, "error");
          }
        });
    } else {
      mySwal.fire("Error", "Please select the redemption", "error");
    }
  };

  const generate = () => {
    if (data.length > 0) {
      setLoading(true);
      const AllHcpIds = [
        ...new Set(allRedemptionIds.map((r) => r.hcp?.id).filter(Boolean)),
      ];
      const sendThis = {
        date,
        hcpIds: AllHcpIds,
        mytype: type,
      };

      axios
        .post("/api/admin/excelgen", sendThis)
        .then((res) => {
          setLoading(false);
          console.log(res.data.url);
          window.location = res.data.url;
        })
        .catch((err) => {
          setLoading(false);
          if (err.response.data.error === "Token Expired") {
            mySwal
              .fire({
                title: "Error",
                text: err.response.data.error,
                icon: "error",
                allowOutsideClick: false,
              })
              .then((click) => {
                if (click.isConfirmed) {
                  navigate("/admin/login");
                }
              });
          } else {
            mySwal.fire("Error", err.response.data.error, "error");
          }
        });
    } else {
      mySwal.fire("Error", "No Approved Redemption", "error");
    }
  };

  return (
    <div className="d-flex">
      <Drawer
        sx={{
          minWidth: "20%",
          "& .MuiDrawer-paper": {
            minWidth: "20%",
            boxSizing: "border-box",
            minHeight: "100vh",
            backgroundColor: "#FE7072",
          },
        }}
        anchor="left"
        open={openSidebar}
        variant="persistent"
      >
        <Sidebar />
      </Drawer>

      <Main open={openSidebar}>
        <div className="mt-3 px-3">
          <Header
            title={
              validationStatus === "Approve" && type === "Zuellig"
                ? "ZP REDEMPTION REPORT"
                : validationStatus === "Approve" && type === "Antah"
                  ? "ANTAH REDEMPTION REPORT"
                  : "VALIDATION"
            }
            openSidebar={openSidebar}
            setOpenSidebar={setOpenSidebar}
          />

          <div className="report-header mt-3 d-flex py-3 px-3 justify-content-end">
            {validationStatus === "Approve" ? (
              <div>
                <input
                  type="date"
                  className="form-control"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
            ) : (
              <div className="d-flex">
                <div>
                  <DatePicker
                    placeholderText="Date"
                    value={
                      startDate
                        ? `${startDate ? new Date(startDate).toLocaleDateString("en-GB") : ""} - ${endDate ? new Date(endDate).toLocaleDateString("en-GB") : ""}`
                        : ""
                    }
                    onChange={handleDate}
                    startDate={startDate}
                    endDate={endDate}
                    selectsRange
                    shouldCloseOnSelect={false}
                    className="form-control"
                  />
                  {!startDate && (
                    <span className="danger-text">
                      Showing latest 3 months result from{" "}
                      {date3monthsAgo.toLocaleDateString("en-GB")}
                    </span>
                  )}
                </div>
                <div className="ps-2">
                  <button
                    type="button"
                    className="btn btn-primary shadow-none admin-button"
                    onClick={() => handleFilter()}
                  >
                    Filter
                  </button>
                </div>
                <div className="ps-2">
                  <button
                    type="button"
                    className="btn btn-primary shadow-none admin-button"
                    onClick={() => handleReset()}
                  >
                    Reset
                  </button>
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="d-flex pt-2">
              <p
                className={
                  type === "Zuellig"
                    ? "validation-title-selected"
                    : "validation-title"
                }
                onClick={() => setType("Zuellig")}
              >
                Zuellig
              </p>
              {/* <div className="validation-vertical-line"></div>
              <p className={type === "Antah" ? "validation-title-selected" : "validation-title"} onClick={() => setType("Antah")}>Antah</p> */}
            </div>

            <div className="d-flex pt-2">
              <p
                className={
                  validationStatus === "Approve"
                    ? "validation-title-selected"
                    : "validation-title"
                }
                onClick={() => setValidationStatus("Approve")}
              >
                Approve
              </p>
              <div className="validation-vertical-line"></div>
              <p
                className={
                  validationStatus === "Pending"
                    ? "validation-title-selected"
                    : "validation-title"
                }
                onClick={() => {
                  setValidationStatus("Pending");
                  setSearchTerm("");
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
              >
                Pending
              </p>
            </div>

            <div className="pt-2">
              {loading ? (
                <div className="text-center">
                  <CircularProgress />
                </div>
              ) : (
                <div>
                  {validationStatus === "Pending" ? (
                    <div>
                      {/* Search Bar */}
                      <Box
                        sx={{
                          mb: 3,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <TextField
                          placeholder="Search by account, code, HCP, SKU, patient, remarks..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          size="small"
                          sx={{ minWidth: "400px" }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <InputLabel>Per Page</InputLabel>
                          <Select
                            value={pagination.pageSize}
                            label="Per Page"
                            onChange={handlePageSizeChange}
                          >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                          </Select>
                        </FormControl>
                        <Typography variant="body2" color="textSecondary">
                          Showing {data.length} of {pagination.total} records
                        </Typography>
                      </Box>

                      {/* Table Section */}
                      {data.length > 0 ? (
                        <Box
                          sx={{
                            "& .dataTables_info, & .dataTables_paginate, & .dataTables_length, & .dataTables_wrapper .dataTables_paginate, & .dataTables_wrapper .dataTables_info":
                              {
                                display: "none !important",
                              },
                            "& .page-item, & .pagination, & .page-link, & .paginate_button":
                              {
                                display: "none !important",
                              },
                            "& .dataTables_wrapper .row:last-child": {
                              display: "none !important",
                            },
                            '& [class*="paginate"]': {
                              display: "none !important",
                            },
                            "& .dt-paging, & .dt-paging-button, & .previous, & .next":
                              {
                                display: "none !important",
                              },
                            "& button[data-dt-idx], & .dataTables_paginate, & .paging_simple_numbers":
                              {
                                display: "none !important",
                              },
                            "& .paginate_button.previous, & .paginate_button.next, & .paginate_button.first, & .paginate_button.last":
                              {
                                display: "none !important",
                              },
                          }}
                        >
                          <MDBDataTableV5
                            data={pendingTableData}
                            key={`table-${pagination.page}-${data.length}-${validationStatus}-${forceTableRender}`}
                            hover
                            responsive
                            checkbox
                            headCheckboxID={`header-checkbox-${pagination.page}`}
                            bodyCheckboxID={`body-checkbox-${pagination.page}`}
                            getValueCheckBox={(e) => {
                              selectRedemption(e);
                            }}
                            getValueAllCheckBoxes={(e) => {
                              selectAllRedemptions(e);
                            }}
                            multipleCheckboxes
                            paging={false}
                            searching={false}
                            entries={false}
                            entriesOptions={false}
                            info={false}
                            paginationLabel={[]}
                            displayEntries={false}
                            pageLength={-1}
                            lengthMenu={[]}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            textAlign: "center",
                            py: 4,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                          }}
                        >
                          <SearchIcon
                            sx={{
                              fontSize: 48,
                              color: "text.secondary",
                              mb: 2,
                            }}
                          />
                          <Typography variant="h6" color="textSecondary">
                            {searchTerm
                              ? `No records found for "${searchTerm}"`
                              : "No data available"}
                          </Typography>
                          {searchTerm && (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ mt: 1 }}
                            >
                              Try adjusting your search terms
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* Pagination - Always show if there are multiple pages */}
                      {pagination.totalPages > 1 && (
                        <Box
                          sx={{
                            mt: 3,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 2,
                          }}
                        >
                          <Typography variant="body2" color="textSecondary">
                            Page {pagination.page} of {pagination.totalPages}(
                            {pagination.total} total records,{" "}
                            {pagination.pageSize} per page)
                          </Typography>
                          <Pagination
                            count={pagination.totalPages}
                            page={pagination.page}
                            onChange={handlePageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                          />
                        </Box>
                      )}

                      {/* Show no data message only when there are no records at all */}
                      {pagination.total === 0 && !loading && (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                          <Typography variant="body2" color="textSecondary">
                            {searchTerm
                              ? `No records found for "${searchTerm}"`
                              : "No data available"}
                          </Typography>
                          {searchTerm && (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ mt: 1 }}
                            >
                              Try adjusting your search terms
                            </Typography>
                          )}
                        </Box>
                      )}
                      <div className="d-flex justify-content-end pb-2 mt-3">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => approve()}
                        >
                          Approve
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* Search Bar for Approved Data */}
                      <Box
                        sx={{
                          mb: 3,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <TextField
                          placeholder="Search approved records..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          size="small"
                          sx={{ minWidth: "300px" }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                          <InputLabel>Per Page</InputLabel>
                          <Select
                            value={pagination.pageSize}
                            label="Per Page"
                            onChange={handlePageSizeChange}
                          >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                          </Select>
                        </FormControl>
                        <Typography variant="body2" color="textSecondary">
                          Showing {data.length} of {pagination.total} records
                        </Typography>
                      </Box>

                      {data.length > 0 ? (
                        <>
                          <Box
                            sx={{
                              "& .dataTables_info, & .dataTables_paginate, & .dataTables_length, & .dataTables_wrapper .dataTables_paginate, & .dataTables_wrapper .dataTables_info":
                                {
                                  display: "none !important",
                                },
                              "& .page-item, & .pagination, & .page-link, & .paginate_button":
                                {
                                  display: "none !important",
                                },
                              "& .dataTables_wrapper .row:last-child": {
                                display: "none !important",
                              },
                              '& [class*="paginate"]': {
                                display: "none !important",
                              },
                              "& .dt-paging, & .dt-paging-button, & .previous, & .next":
                                {
                                  display: "none !important",
                                },
                              "& button[data-dt-idx], & .dataTables_paginate, & .paging_simple_numbers":
                                {
                                  display: "none !important",
                                },
                              "& .paginate_button.previous, & .paginate_button.next, & .paginate_button.first, & .paginate_button.last":
                                {
                                  display: "none !important",
                                },
                            }}
                          >
                            <MDBDataTableV5
                              data={approveTableData}
                              hover
                              responsive
                              bordered
                              paging={false}
                              searching={false}
                              entries={false}
                              entriesOptions={false}
                              info={false}
                              paginationLabel={[]}
                              displayEntries={false}
                              pageLength={-1}
                              lengthMenu={[]}
                            />
                          </Box>

                          {/* Pagination */}
                          <Box
                            sx={{
                              mt: 3,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              flexWrap: "wrap",
                              gap: 2,
                            }}
                          >
                            <Typography variant="body2" color="textSecondary">
                              Page {pagination.page} of {pagination.totalPages}(
                              {pagination.total} total records,{" "}
                              {pagination.pageSize} per page)
                            </Typography>
                            <Pagination
                              count={pagination.totalPages}
                              page={pagination.page}
                              onChange={handlePageChange}
                              color="primary"
                              showFirstButton
                              showLastButton
                            />
                          </Box>
                        </>
                      ) : (
                        <Box
                          sx={{
                            textAlign: "center",
                            py: 4,
                            border: "1px solid #e0e0e0",
                            borderRadius: 1,
                          }}
                        >
                          <SearchIcon
                            sx={{
                              fontSize: 48,
                              color: "text.secondary",
                              mb: 2,
                            }}
                          />
                          <Typography variant="h6" color="textSecondary">
                            {searchTerm
                              ? `No approved records found for "${searchTerm}"`
                              : "No approved data available"}
                          </Typography>
                        </Box>
                      )}
                      {type === "Zuellig" ? (
                        <div>
                          <p className="admin-dashboard-text">
                            <b>ZUELLIG ORDER FORM</b>
                          </p>
                          <div className="d-flex">
                            <div className="pe-2" style={{ margin: "auto" }}>
                              {hcpNameList.length > 0
                                ? hcpNameList.map((hcp, i) => {
                                    return (
                                      <div key={i} className="pb-2">
                                        <button
                                          type="button"
                                          className={
                                            selectedHcpIndex === i
                                              ? "btn btn-primary admin-button shadow-none form-control"
                                              : "btn btn-primary admin-cancel-btn shadow-none form-control"
                                          }
                                          onClick={() => setSelectedHcpIndex(i)}
                                        >
                                          {hcp.hcpName}
                                        </button>
                                      </div>
                                    );
                                  })
                                : null}
                            </div>
                            <div style={{ margin: "auto" }}>
                              <p style={{ fontSize: "28px" }}>
                                <u>
                                  <b>ZPFC : PATIENT PROGRAM ORDER FORM</b>
                                </u>{" "}
                                (for sales rep only)
                              </p>
                              <p
                                style={{ fontSize: "18px", lineHeight: "8px" }}
                              >
                                email to : rpa-order@zuelligpharma.com
                              </p>
                              <table
                                className="table table-bordered mt-5"
                                style={{ borderColor: "black" }}
                              >
                                <thead>
                                  <tr>
                                    <th scope="col">Order Form Reference</th>
                                    <th scope="col">RPA-PP1Rep</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <th>ZP Client Code</th>
                                    <td>104590</td>
                                  </tr>
                                  <tr>
                                    <th>Sales Rep Name</th>
                                    <td>
                                      {hcpNameList.length > 0
                                        ? hcpNameList[selectedHcpIndex].cvseName
                                        : ""}
                                    </td>
                                  </tr>
                                  <tr>
                                    <th>Sales Rep Contact</th>
                                    <td>
                                      {hcpNameList.length > 0
                                        ? hcpNameList[selectedHcpIndex]
                                            .cvseContact
                                        : ""}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                              <table
                                className="table table-bordered mt-5"
                                style={{ borderColor: "black" }}
                              >
                                <thead>
                                  <tr>
                                    <th scope="col">ZP SKU Code</th>
                                    <th scope="col">Order (Qty)</th>
                                    <th scope="col">Sold-To Account</th>
                                    <th scope="col">Ship-To Account</th>
                                    <th scope="col">Cust PO / Ref No</th>
                                    <th scope="col">
                                      Delivery Instructions (for Transporter
                                      only)
                                    </th>
                                    <td></td>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td></td>
                                    <td></td>
                                    <td>
                                      {hcpNameList.length > 0
                                        ? hcpNameList[selectedHcpIndex]
                                            .soldToCode
                                        : ""}
                                    </td>
                                    <td>
                                      {hcpNameList.length > 0
                                        ? hcpNameList[selectedHcpIndex]
                                            .shipToCode
                                        : ""}
                                    </td>
                                    <td></td>
                                    <td>
                                      iCARE-
                                      {new Date().toISOString().substring(5, 7)}
                                      -{new Date().getFullYear()}; Attn to:{" "}
                                      {hcpNameList.length > 0
                                        ? hcpNameList[selectedHcpIndex].hcpPic
                                        : ""}
                                    </td>
                                    <td>
                                      {hcpNameList.length > 0
                                        ? hcpNameList[selectedHcpIndex].hcpName
                                        : ""}
                                    </td>
                                  </tr>
                                  {hcpNameList.length > 0
                                    ? hcpNameList[
                                        selectedHcpIndex
                                      ].redemptions.map((redemption, i) => {
                                        return (
                                          <tr key={i}>
                                            <td>{redemption.skuCode}</td>
                                            <td>{redemption.qty}</td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td>
                                              {redemption.patientLastFourDigit}
                                            </td>
                                            <td>{redemption.skuRedeemed}</td>
                                          </tr>
                                        );
                                      })
                                    : null}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          <div className="d-flex justify-content-end pb-2">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => generate()}
                            >
                              Generate
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p className="admin-dashboard-text">
                            <b>ANTAH ORDER FORM</b>
                          </p>
                          <div className="d-flex">
                            <div className="pe-2" style={{ margin: "auto" }}>
                              {hcpNameList.length > 0
                                ? hcpNameList.map((hcp, i) => {
                                    return (
                                      <div key={i} className="pb-2">
                                        <button
                                          type="button"
                                          className={
                                            selectedHcpIndex === i
                                              ? "btn btn-primary admin-button shadow-none form-control"
                                              : "btn btn-primary admin-cancel-btn shadow-none form-control"
                                          }
                                          onClick={() => setSelectedHcpIndex(i)}
                                        >
                                          {hcp.hcpName}
                                        </button>
                                      </div>
                                    );
                                  })
                                : null}
                            </div>
                            <div style={{ margin: "auto" }}>
                              <p style={{ fontSize: "28px" }}>
                                <b>
                                  <i>
                                    ANTAH PHARMA SDN BHD{" "}
                                    <small>(118544-V)</small>
                                  </i>
                                </b>
                              </p>
                              <p
                                style={{ fontSize: "24px", lineHeight: "8px" }}
                              >
                                STOCK ISSUE REQUISITION (SIR)
                              </p>
                              <div className="d-flex">
                                <div
                                  className="mx-3"
                                  style={{
                                    border: "1px solid black",
                                    padding: "2% 5%",
                                  }}
                                >
                                  <p>
                                    <b>Customer name &amp; address</b>
                                  </p>
                                  <p>
                                    {hcpNameList.length > 0
                                      ? hcpNameList[selectedHcpIndex]
                                          .purchasecode
                                      : ""}
                                  </p>
                                  <p style={{ lineHeight: "10px" }}>
                                    {hcpNameList.length > 0
                                      ? hcpNameList[selectedHcpIndex]
                                          .purchaseName
                                      : ""}
                                  </p>
                                  <p>
                                    {hcpNameList.length > 0
                                      ? hcpNameList[selectedHcpIndex].hcpAddress
                                      : ""}
                                  </p>
                                  <br />
                                  <p>
                                    <b>Contact person: </b>
                                    {hcpNameList.length > 0
                                      ? hcpNameList[selectedHcpIndex].hcpPic
                                      : ""}
                                  </p>
                                  <p>
                                    <b>Contact number: </b>
                                    {hcpNameList.length > 0
                                      ? hcpNameList[selectedHcpIndex].hcpContact
                                      : ""}
                                  </p>
                                </div>
                                <div>
                                  <div
                                    className="container"
                                    style={{ border: "1px solid black" }}
                                  >
                                    <p>
                                      Date:{" "}
                                      {new Date()
                                        .toISOString()
                                        .substring(0, 10)}
                                    </p>
                                  </div>
                                  <div
                                    className="container mt-2"
                                    style={{ border: "1px solid black" }}
                                  >
                                    <p>SIA No: </p>
                                  </div>
                                </div>
                              </div>
                              <table
                                className="table table-bordered mt-3"
                                style={{ borderColor: "black" }}
                              >
                                <thead>
                                  <tr>
                                    <th scope="col">No.</th>
                                    <th scope="col">Item code</th>
                                    <th scope="col">
                                      Description &amp; Batch No/Expiry
                                    </th>
                                    <th scope="col">Qty</th>
                                    <th scope="col">
                                      Unit Cost / Selling Price
                                    </th>
                                    <th scope="col">
                                      Total Cost / Selling Price
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {hcpNameList.length > 0
                                    ? hcpNameList[
                                        selectedHcpIndex
                                      ].redemptions.map((redemption, i) => {
                                        return (
                                          <tr key={i}>
                                            <th scope="row">{i + 1}</th>
                                            <td>{redemption.skuCode}</td>
                                            <td>{redemption.skuRedeemed}</td>
                                            <td>1</td>
                                            <td></td>
                                            <td></td>
                                          </tr>
                                        );
                                      })
                                    : null}
                                </tbody>
                              </table>
                            </div>
                          </div>
                          <div className="d-flex justify-content-end pb-2">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => generate()}
                            >
                              Generate
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Main>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    token: state.admin.token,
    role: state.admin.role,
  };
};

export default connect(mapStateToProps, null)(Validation);
